/* eslint-disable jsdoc/require-param-type */
/* eslint-disable jsdoc/require-param-description */
import React, { useEffect, useState } from "react";
import { Button, Grid, TextField, MenuItem } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  provinces,
  countryCodes,
} from "../../../../../components/shared/constants";

const useStyles = makeStyles(theme => ({
  formContainer: {
    marginTop: theme.spacing(2),
  },
  buttons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(3),
  },
  buttonRight: {
    marginLeft: "auto",
  },
}));

/**
 * AddressInfoForm component - simplified validation approach
 * @param root0
 * @param root0.formData
 * @param root0.onSave
 * @param root0.onBack
 * @param root0.onNext
 * @param root0.submitLabel
 * @param root0.showBackButton
 */
export default function AddressInfoForm({
  formData,
  onSave,
  onBack,
  onNext,
  submitLabel = "Save & Continue",
  showBackButton = true,
}) {
  const classes = useStyles();

  // Local state for form values
  const [values, setValues] = useState({
    province: formData.province || "",
    street: formData.street || "",
    suburb: formData.suburb || "",
    city: formData.city || "",
    postalCode: formData.postalCode || "",
    country: formData.country || "ZAF",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    setValues({
      province: formData.province || "",
      street: formData.street || "",
      suburb: formData.suburb || "",
      city: formData.city || "",
      postalCode: formData.postalCode || "",
      country: formData.country || "ZAF",
    });
  }, [formData]);

  // Handle input changes
  const handleChange = field => event => {
    const { value } = event.target;
    const newValues = { ...values, [field]: value };
    
    setValues(newValues);

    // Auto-save to parent immediately
    if (onSave) {
      const dataToSave = {
        ...formData,
        ...newValues,
      };
      onSave(dataToSave);
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Handle postal code change (numeric only)
  const handlePostalCodeChange = event => {
    const numericValue = event.target.value.replace(/[^0-9]/g, "");
    if (numericValue.length <= 4) {
      const newValues = { ...values, postalCode: numericValue };
      
      setValues(newValues);

      // Auto-save to parent immediately
      if (onSave) {
        const dataToSave = {
          ...formData,
          ...newValues,
        };
        onSave(dataToSave);
      }

      // Clear error
      if (errors.postalCode) {
        setErrors(prev => ({ ...prev, postalCode: "" }));
      }
    }
  };

  // Basic validation
  const validateForm = () => {
    const newErrors = {};

    if (!values.province) newErrors.province = "Province is required";
    if (!values.street?.trim()) newErrors.street = "Street address is required";
    if (!values.suburb?.trim()) newErrors.suburb = "Suburb is required";
    if (!values.city?.trim()) newErrors.city = "City is required";
    if (!values.postalCode?.trim()) {
      newErrors.postalCode = "Postal code is required";
    } else if (values.postalCode.length !== 4) {
      newErrors.postalCode = "Postal code must be 4 digits";
    }
    if (!values.country) newErrors.country = "Country is required";

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Basic validation
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Save data first
    if (onSave) {
      onSave(values);
    }

    // Move to next step
    if (onNext) {
      onNext(values);
    }
  };

  return (
    <form className={classes.formContainer} onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Street Address */}
        <Grid item xs={12}>
          <TextField
            name="street"
            label="Street Address"
            autoComplete="street-address"
            variant="outlined"
            fullWidth
            required
            value={values.street}
            onChange={handleChange("street")}
            error={!!errors.street}
            helperText={errors.street}
          />
        </Grid>

        {/* Suburb */}
        <Grid item xs={12} md={6}>
          <TextField
            name="suburb"
            label="Suburb"
            autoComplete="address-level2"
            variant="outlined"
            fullWidth
            required
            value={values.suburb}
            onChange={handleChange("suburb")}
            error={!!errors.suburb}
            helperText={errors.suburb}
          />
        </Grid>

        {/* City */}
        <Grid item xs={12} md={6}>
          <TextField
            name="city"
            label="City"
            autoComplete="address-level1"
            variant="outlined"
            fullWidth
            required
            value={values.city}
            onChange={handleChange("city")}
            error={!!errors.city}
            helperText={errors.city}
          />
        </Grid>

        {/* Province */}
        <Grid item xs={12} md={4}>
          <TextField
            select
            name="province"
            label="Province"
            variant="outlined"
            fullWidth
            required
            value={values.province}
            onChange={handleChange("province")}
            error={!!errors.province}
            helperText={errors.province}
          >
            <MenuItem value="">
              <em>Select a province</em>
            </MenuItem>
            {provinces.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Postal Code */}
        <Grid item xs={12} md={4}>
          <TextField
            name="postalCode"
            label="Postal Code"
            autoComplete="postal-code"
            variant="outlined"
            fullWidth
            required
            value={values.postalCode}
            onChange={handlePostalCodeChange}
            error={!!errors.postalCode}
            helperText={errors.postalCode || "4 digits"}
            inputProps={{
              maxLength: 4,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
        </Grid>

        {/* Country */}
        <Grid item xs={12} md={4}>
          <TextField
            select
            name="country"
            label="Country"
            variant="outlined"
            fullWidth
            required
            value={values.country}
            onChange={handleChange("country")}
            error={!!errors.country}
            helperText={errors.country}
          >
            {countryCodes.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <div className={classes.buttons}>
        {showBackButton && (
          <Button onClick={onBack} disabled={!onBack}>
            Back
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          type="submit"
          className={!showBackButton ? classes.buttonRight : ""}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
